import { useState } from 'react'
import { Link } from 'react-router-dom'
import { usePools } from '@/hooks/use-pools'
import { useAccounts } from '@/hooks/use-accounts'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Loader2, Copy, Check, ArrowRight } from 'lucide-react'
import { AccountIcon } from '@/lib/entity-icons'
import { useIncognito } from '@/hooks/use-incognito'

export default function Accounts() {
  const { data: pools = [], isLoading: loading } = usePools()
  const incognito = useIncognito()
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const accountIDs = [...new Set(pools.map((p) => p.accountID))]
  const accounts = useAccounts(accountIDs)

  const handleCopyId = async (e: React.MouseEvent, id: string) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(id)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      // ignore
    }
  }

  // Build per-account summary from pool list
  const accountSummaries = accountIDs.map((accountID) => ({
    accountID,
    account: accounts[accountID],
    pools: pools.filter((p) => p.accountID === accountID),
  }))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Accounts</h1>
        <p className="text-muted-foreground">Accounts associated with your pools</p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty */}
      {!loading && accountSummaries.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AccountIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No accounts found</h3>
            <p className="text-sm text-muted-foreground">
              Accounts will appear here once pools have been created.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Account cards */}
      {!loading && accountSummaries.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {accountSummaries.map(({ accountID, account, pools: accountPools }) => (
            <Link key={accountID} to={`/accounts/${accountID}`} className="block h-full group">
              <Card className="h-full transition-colors group-hover:border-foreground/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {!incognito.isIncognito && account?.customization?.logo
                      ? <img src={account.customization.logo} alt="" className="h-5 w-auto max-w-[56px] object-contain flex-shrink-0" />
                      : <AccountIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    }
                    <span className="truncate flex-1">
                      {incognito.isIncognito
                        ? incognito.accountName(accountID)
                        : (account?.name ?? '…')}
                    </span>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </CardTitle>
                  <CardDescription className="font-mono text-xs flex items-center gap-2">
                    {!incognito.isIncognito && (
                      <>
                        <span className="truncate">{accountID}</span>
                        <button
                          onClick={(e) => handleCopyId(e, accountID)}
                          className="flex-shrink-0 p-1 hover:bg-muted rounded transition-colors"
                          title="Copy Account ID"
                        >
                          {copiedId === accountID ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                          )}
                        </button>
                      </>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                    {accountPools.length} pool{accountPools.length !== 1 ? 's' : ''}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
